import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { Box, Flex } from 'reflexbox';
import { Form, FormikProvider, useFormik } from 'formik';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import {
	PageTitle,
	SectionTitle,
	SubSectionTitle,
} from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import useBreadcrumb from 'packages/kakapo-ui-kit/components/breadcrumb/useBreadcrumb';
import { ActionArchiveListItem } from 'packages/kakapo-ui-kit/components/actions/ActionListItem';

export function JobLevelEditor(props: { jobLevel?: graphql.JobLevel }) {
	const { t } = useTranslation();
	const [error, attempt] = useErrorBoundary();
	const { addToast } = useToasts();
	const history = useHistory();

	const [create, { loading: createLoading }] = graphql.useCreateJobLevelMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateJobLevelMutation();

	const formik = useFormik({
		initialValues: {
			title: props.jobLevel?.title ?? [],
			description: props.jobLevel?.description ?? [],
		},
		onSubmit: attempt(async () => {
			if (props.jobLevel) {
				await update({
					variables: {
						input: {
							id: props.jobLevel.id,
							title: formik.values.title,
							description: formik.values.description,
						},
					},
				});
			} else {
				const response = await create({
					variables: {
						input: {
							title: formik.values.title,
							description: formik.values.description,
						},
					},
				});

				history.push(`/settings/job-levels/${response.data?.createJobLevel.jobLevel.id}`);
			}

			addToast(t('common.settings.jobLevels.edit.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
		}),
	});

	const loading = createLoading || updateLoading;

	useBreadcrumb(t(`common.settings.jobLevels.${props.jobLevel ? 'edit' : 'create'}.title`));

	return (
		<Flex flexDirection="column" mt={3}>
			<Flex flexDirection="row" alignItems="center">
				<PageTitle>
					{t(`common.settings.jobLevels.${props.jobLevel ? 'edit' : 'create'}.title`)}
				</PageTitle>
			</Flex>
			<Box mt={4} mb={2}>
				<Box mb={3}>
					<SubSectionTitle>{t('common.settings.jobLevels.edit.details')}</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				<Form>
					<DetailsTableForm
						formValues={[
							{
								label: t('common.settings.jobLevels.edit.name'),
								name: 'title',
								type: DetailsTableFormValueTypes.Multilingual,
							},
							{
								label: t('common.settings.jobLevels.edit.description'),
								name: 'description',
								type: DetailsTableFormValueTypes.Multilingual,
							},
						]}
					/>
				</Form>
			</FormikProvider>

			<Flex mt={4}>
				<Button
					size={32}
					onClick={() => formik.submitForm()}
					isLoading={loading}
					enabled={!!formik.values.title.length}
				>
					{t('common.save')}
				</Button>
			</Flex>
			{props.jobLevel && (
				<Box mt={4}>
					<Box my={2}>
						<SectionTitle>{t('common.settings.jobLevels.actions.title')}</SectionTitle>
					</Box>

					<Box mb={3}>
						<HorizontalRule />
					</Box>

					<ActionArchiveListItem
						archivableItem={props.jobLevel}
						archivableItemName={t('common.settings.jobLevels.archivableItemName')}
						archivableItemKey="jobLevel"
						useArchive={graphql.useArchiveJobLevelMutation as any}
						useRestore={graphql.useRestoreJobLevelMutation as any}
					/>
				</Box>
			)}
		</Flex>
	);
}

export default function JobLevelEditorWithID(props: { id: string }) {
	const { data } = graphql.useJobLevelQuery({
		variables: {
			id: props.id,
		},
		fetchPolicy: 'network-only',
	});

	if (!data) {
		return <Spinner isLoading size="18px" />;
	}

	return <JobLevelEditor jobLevel={data.jobLevel as graphql.JobLevel} />;
}
